import { useAuthContext } from 'contexts/AuthContext';
import FHIRPoster from 'data/FHIRPoster';
import FHIRUpdater from 'data/FHIRUpdater';
import type { Reference } from 'fhir/r4';
import { FHIRUtils, ObservationBuilder } from 'fhir-sdoh';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import SemanticDatepicker from 'react-semantic-ui-datepickers';
import { HorizontalGridLines, XYPlot, XAxis, YAxis, LineMarkSeries, LabelSeries } from 'react-vis';
import { Button, Form, Icon, Label, Loader, Popup } from 'semantic-ui-react';
import { parseFHIRLab } from 'utils/FHIRUtils';
import type { ResourceEntry } from 'utils/bundleNarrowing';
import { dropdownValue } from 'utils/dropdownValue';
import { softDeleteObservation } from 'utils/softDeleteObservation';

import type { ProfileChildBaseProps, ProfileMutate, ProfileObservationEntry, RejectionWithMessage } from './types';

/**
 * `patientId` and `mutate` are required: both flow unguarded into
 * `ObservationBuilder`/`softDeleteObservation` and `props.mutate().then(...)`.
 * See the defect note in `vitals.tsx` — `patientId` originates as FHIR's `0..1`
 * `Patient.id`.
 *
 * `data` is required: the highlight `useEffect` opens with `props.data[0]` and
 * the `glucoseOnly` filter runs `props?.data.filter(...)` during render, both
 * before the `{props.data && ...}` guard in the returned tree. An absent `data`
 * throws rather than rendering the empty state.
 */
type LabsProps = Omit<ProfileChildBaseProps, 'patientId'> & {
  /** The member's FHIR `Patient.id`. */
  patientId: string;
  /** Lab-coded Observation entries from `Profile.getDataByCode`. */
  data: ProfileObservationEntry[];
  /** The comma-separated lab codes this panel renders; accepted but unread. */
  code?: string;
  /** Revalidates the profile query after a lab is created, updated or deleted. */
  mutate: ProfileMutate;
  /** Switches the patient view to the surveys tab. */
  setActiveTab?: ((tab: string) => void) | undefined;
  /** Opens a specific questionnaire response, or a new one when `true`. */
  updateQRID?: ((id: string, isNewResponse: boolean) => void) | undefined;
};

function Labs(props: LabsProps) {
  const [loading, setLoading] = React.useState(!props.data);
  //for lab options, whether enter a1c value or blood sugar survey
  const [popupOpen, setPopupOpen] = React.useState(false);
  // These five seed at null and are written from two sources: parsed observation data
  // (a Date for the reading date, a number for the value) and semantic-ui field `value`s.
  const [readingDate, setReadingDate] = useState<string | number | Date | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [a1cvalue, setA1CValue] = useState<string | number | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [readingType, setReadingType] = useState<string | null>(null);
  const [selectedObservation, setSelectedObservation] = useState<{
    resource: fhir4.Observation;
  } | null>(null);

  const [graphView, setGraphView] = useState(false);
  const [filterGlucose, setFilterGlucose] = useState(false);

  const authInfo = useAuthContext();

  const [highlightID, setHighlightID] = React.useState<(string | undefined)[]>([]);

  useEffect(() => {
    if (props.data[0]) {
      let lastDataInfo = '';
      let indexOfQuestionnaire = '';
      let currentDataInfo = '';
      let lastUpdatedDate = '';
      let tempHighlightId = highlightID;

      for (let i = 0; i < props.data.length; i++) {
        //if the data does have surveyResponse id, we will use the surveyResponse id as lastDataInfo
        if ((props?.data[i]?.resource?.derivedFrom?.length ?? 0) > 0) {
          if (i === 0) {
            // Every `?.` chain in this loop can resolve `undefined` into a local
            // seeded `''`/`-1`. A `?? ''` would change which value the comparisons
            // below see.
            lastUpdatedDate = props?.data[i]?.resource?.effectivePeriod?.start as string;
          }
          indexOfQuestionnaire = '';
          currentDataInfo = '';
          indexOfQuestionnaire = props?.data[i]?.resource?.['derivedFrom']!.findIndex(
            (obj: Reference) => (obj.display = 'Questionnaire'),
          ) as unknown as string;
          currentDataInfo = props?.data[i]?.resource?.['derivedFrom']?.[
            indexOfQuestionnaire as unknown as number
          ]?.['reference']?.split('/')?.[1] as string;
        } else {
          //if the data does not have surveyResponse id, we will use the effectivePeriod as lastDataInfo
          currentDataInfo = props?.data[i]?.resource?.effectivePeriod?.start as string;
        }
        if (!lastDataInfo) {
          lastDataInfo = currentDataInfo;
        }
        if (
          (currentDataInfo === lastDataInfo &&
            moment(lastUpdatedDate).diff(
              props?.data[i]?.resource?.effectivePeriod?.start,
              'seconds',
            ) < 15) ||
          moment(lastDataInfo).diff(currentDataInfo, 'seconds') < 15
        ) {
          if (!highlightID.includes(props?.data[i]?.resource?.id)) {
            tempHighlightId.push(props?.data[i]?.resource?.id);
            setHighlightID(tempHighlightId);
          }
        } else {
          if (highlightID.includes(props?.data[i]?.resource?.id)) {
            tempHighlightId = tempHighlightId.filter((obs) => {
              return obs !== props?.data[i]?.resource?.id;
            });
            setHighlightID(tempHighlightId);
          }
        }
      }
    }
  }, [props?.data]);
  /*
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. Two assertions here throw on FHIR
   * shapes the server is entitled to send, and because this is a `filter`
   * predicate the throw escapes the whole Labs render rather than dropping one
   * row: the care manager sees the profile subtree disappear, not a short list.
   *
   * `CodeableConcept.coding` is `0..*` in the installed `@types/fhir@0.0.34`
   * (`coding?: Coding[] | undefined`), so `coding![0]` is `undefined[0]` —
   * `TypeError` — for an Observation coded by `code.text` alone. `Coding.display`
   * is `0..1`, so `display!.toLowerCase()` throws for a coding that carries
   * `system` + `code` without a human label, which is the common machine-to-machine
   * shape. `Observation.code` itself is `1..1`, so it is read unchained.
   *
   * DIVERGENCE FROM SOURCE — this predicate is NOT verbatim, and the difference is
   * a behaviour change. Upstream at `41ee0e63` is one expression:
   *
   *     obs?.resource?.code?.coding[0]?.display.toLowerCase().includes('glucose')
   *
   * which throws for an Observation coded by `code.text` alone. The `if
   * (!firstCoding) return undefined;` below does NOT throw: it drops that row
   * from the Glucose panel silently. So a real glucose reading carrying
   * `code.text` but no `coding` array is charted upstream's way (loudly, as a
   * failed render) and simply vanishes here — the outcome the paragraph above
   * names as the reason not to apply this repair. Kept deliberately rather than
   * reverted, because reinstating the throw blanks the whole Labs subtree; it is
   * disclosed here and on the PR rather than presented as preserved behaviour.
   *
   * The second assertion is untouched: `display!` still throws for a coding with
   * `system` + `code` and no human label.
   */
  const glucoseOnly = props?.data.filter((obs: ResourceEntry<'Observation'>) => {
    // Verbatim upstream (`develop:src/components/patient/profile/labs.tsx:80` @ `f46fc9519`),
    // with only the assertions that erase. An earlier form guarded with
    // `if (!firstCoding) return undefined;`, which SKIPPED an Observation coded by `code.text`
    // alone instead of throwing — a real behaviour change: upstream fails the Labs render
    // loudly, the guard made a glucose reading vanish from the panel silently.
    //
    // The guard was kept for a while on the grounds that the crash is worse for the user. That
    // is a §0.4 worth argument, and the contract's answer to it is to REPORT the defect, not to
    // change the behaviour — see the SUSPECTED DEFECT above, which is where that judgement now
    // lives. `coding` is `0..1` and `Coding.display` is `0..1`, so both assertions below are the
    // throws upstream already has.
    return obs?.resource?.code?.coding![0]?.display!.toLowerCase().includes('glucose');
  });

  const LAB_RANGES: Record<string, Record<string, number> | undefined> = {
    Glucose: {
      min: 70,
      max: 99,
      'borderline-high': 125,
      high: 126,
    },
    'Systolic blood pressure': {
      max: 119,
      'Pre-Hypertension': 139,
      Hypertension: 179,
      'Hypertensive Crisis': 180,
    },
    'Diastolic blood pressure': {
      max: 79,
      'Pre-Hypertension': 89,
      Hypertension: 119,
      'Hypertensive Crisis': 120,
    },
    'Hemoglobin A1c/Hemoglobin. total in Blood': {
      max: 5.6,
      'borderline-high': 6.4,
      high: 6.5,
    },
    'CO2 SerPl-sCnc': {
      min: 23,
      max: 29,
    },
    'Cholesterol in HDL': {
      min: 60,
      'borderline-low': 41,
      low: 40,
    },
    'Creatinine SerPl-mCnc': {
      min: 0.6,
      max: 1.3,
    },
    'Potassium SerPl-sCnc': {
      min: 3.5,
      max: 5,
    },
    'Triglyceride SerPl-mCnc': {
      max: 149,
      'borderline-high': 199,
      high: 200,
    },
    'Sodium-Ser/Plas': {
      min: 136,
      max: 146,
    },
    LDL: {
      max: 99,
      'borderline-high': 159,
      high: 160,
    },
    'BUN SerPl-mCnc': {
      min: 6,
      max: 21,
    },
    'Chloride SerPl-sCnc': {
      min: 95,
      max: 105,
    },
    'Albumin SerPl-mCnc': {
      min: 3.5,
      max: 5.5,
    },
    'Calcium SerPl-mCnc': {
      min: 8.5,
      max: 10.5,
    },
    'ALT SerPl-cCnc': {
      min: 7,
      max: 56,
    },
    'Total Cholesterol': {
      max: 199,
      'borderline-high': 239,
      high: 240,
    },
    eGFR: {
      min: 90,
    },
    uACR: {
      max: 30,
    },
  };

  const LAB_ALERT = (labName: string, labResult: number) => {
    if (LAB_RANGES[labName]) {
      const single_lab_range = LAB_RANGES[labName];
      if ('min' in single_lab_range && labResult < single_lab_range['min']) {
        if (single_lab_range['borderline-low'] && labResult > single_lab_range['borderline-low']) {
          return 'Borderline-L';
        } else {
          return 'L';
        }
      } else if ('max' in single_lab_range && labResult > single_lab_range['max']) {
        if (
          single_lab_range['borderline-high'] &&
          labResult < single_lab_range['borderline-high']
        ) {
          return 'Borderline-H';
        } else if (
          single_lab_range['Pre-Hypertension'] &&
          labResult < single_lab_range['Pre-Hypertension']
        ) {
          return 'Pre-Hypertension';
        } else if (
          single_lab_range['Hypertension'] &&
          labResult < single_lab_range['Hypertension']
        ) {
          return 'Hypertension';
        } else if (
          single_lab_range['Hypertensive Crisis'] &&
          labResult < single_lab_range['Hypertensive Crisis']
        ) {
          return 'Hypertensive Crisis';
        } else {
          return 'H';
        }
      }
      return '';
    } else {
      return 'No reference';
    }
  };

  const handleOpen = () => {
    setPopupOpen(true);
  };

  const handleClose = () => {
    setPopupOpen(false);
    setReadingDate(null);
    setErrorMessage(null);
    setA1CValue(null);
    setReadingDate(null);
    setReadingType(null);
    setSelectedObservation(null);
  };

  // `target` is cast at the read rather than intersected into the signature: a
  // handler demanding more than `Dropdown`'s `onChange` promises is not assignable
  // to the prop type.
  const handleSelect = (e: React.SyntheticEvent<HTMLElement>) => {
    setReadingType((e.target as HTMLElement).textContent);
  };

  const observationsList = (observation: ProfileObservationEntry, i: number, _counter: number) => {
    const parsedObservation = parseFHIRLab(observation?.resource);
    // `FlatObservation.quantity` is `string`; `LAB_ALERT` compares it with `<`/`>`,
    // which coerces. The cast names that coercion rather than performing one.
    const result = LAB_ALERT(parsedObservation?.display, parsedObservation?.quantity as unknown as number);
    if (!props.sidebar) {
      return (
        <Popup
          key={i}
          flowing
          trigger={
            <Label
              key={'display' + i}
              style={{ margin: '0.15em' }}
              color={highlightID?.includes(observation?.resource?.id) ? 'teal' : 'grey'}
            >
              <div style={{ display: 'flex' }}>
                <div>
                  <b>
                    {parsedObservation.display}
                    {': '} {parsedObservation.quantity} {parsedObservation.unit}
                    {result ? (
                      <span className="bold" style={{ color: '#B03060' }}>
                        {' '}
                        ({result})
                      </span>
                    ) : (
                      ''
                    )}
                  </b>

                  <div>
                    {moment(parsedObservation.effectiveDate).format('MMMM Do YYYY, h:mm:ss a')}
                  </div>
                  {parsedObservation.source && <div>Source: {parsedObservation.source}</div>}
                </div>

                <div style={{ width: 10 }} />

                {parsedObservation.derivedFrom && (
                  <Popup
                    flowing
                    trigger={
                      <Icon
                        size="large"
                        name={'external square'}
                        onClick={() => {
                          /*
                            SUSPECTED DEFECT — REPORTED, NOT FIXED. `setActiveTab`
                            and `updateQRID` are optional and reach this panel from
                            `./index.tsx`'s own optional props, so they are absent
                            wherever the profile is rendered outside the tabbed
                            patient view — the sidebar summary, and every spec that
                            mounts `<Labs>` directly. The control that calls them is
                            rendered on the observation's shape alone, so clicking
                            through to the survey there throws "not a function".
                            The same pair is called unguarded again lower down, on
                            the "Go to Blood Sugar Survey" button.
                          */
                          props.setActiveTab!('SURVEYS');
                          props.updateQRID!(parsedObservation.derivedFrom.substring(22), false);
                        }}
                      />
                    }
                    content={<span>Go to Survey Response</span>}
                    position="bottom left"
                    on="hover"
                  />
                )}
                <Popup
                  flowing
                  trigger={<Icon style={{ float: 'right' }} size="large" name="edit" />}
                  content={
                    <Form onSubmit={handleUpdate} textAlign="center">
                      <Form.Field>
                        <div
                          className="inline fields ui center aligned"
                          style={{
                            display: 'flex',
                            alignContent: 'stretch',
                            justifyContent: 'center',
                            alignSelf: 'right',
                            width: '110%',
                          }}
                        >
                          <Form.Input
                            placeholder="Enter A1C Value"
                            value={a1cvalue}
                            onChange={(e, fieldprops) => {
                              if (!isNaN(Number(fieldprops.value))) {
                                setA1CValue(fieldprops.value);
                                setErrorMessage(null);
                              } else {
                                setA1CValue(fieldprops.value);
                                setErrorMessage('Please enter a number');
                              }
                            }}
                            className="field fluid"
                            textAlign="center"
                            icon="lab"
                          />
                          {errorMessage && (
                            <Label basic color="red" pointing>
                              {errorMessage}
                            </Label>
                          )}
                          <Form.Dropdown
                            placeholder="Select Reading Type"
                            {...dropdownValue(readingType)}
                            selection
                            selectOnBlur={false}
                            options={[
                              {
                                key: 'Self-reported',
                                text: 'Self-reported',
                                value: 'Self-reported',
                              },
                              {
                                key: 'Clinically validated',
                                text: 'Clinically validated',
                                value: 'Clinically validated',
                              },
                            ]}
                            onChange={handleSelect}
                          />
                          <Form.Input
                            // `Form.Input`'s `value` is `string | number`; React
                            // stringifies the `Date`, which is what the field shows.
                            value={
                              // The rule reads the receiver; `Date`'s constructor
                              // rejects the `null` this state admits without the `!`.
                              // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                              new Date(readingDate!) as unknown as string
                            }
                            onChange={(e, fieldprops) => {
                              setReadingDate(fieldprops.value);
                            }}
                            name="readingDate"
                            control={SemanticDatepicker}
                            format="YYYY-MM-DD"
                            placeholder="Enter Reading Date"
                            textAlign="center"
                          />
                        </div>
                        <div className="center aligned">
                          <Button.Group
                            style={{
                              display: 'flex',
                              alignContent: 'stretch',
                              justifyContent: 'center',
                              width: '100%',
                            }}
                            widths="2"
                          >
                            <Form.Button
                              style={{ textalign: 'center' }}
                              color="blue"
                              content="Update A1C Reading"
                              position="center"
                              className={isNaN(Number(a1cvalue)) ? 'disabled' : ''}
                              size="small"
                              textAlign="center"
                              fluid
                            />
                          </Button.Group>
                        </div>
                      </Form.Field>
                    </Form>
                  }
                  open={selectedObservation?.resource.id === observation?.resource.id}
                  onOpen={() => {
                    setSelectedObservation(observation);
                    setA1CValue(Number(parsedObservation.quantity));
                    setReadingType(parsedObservation.readingType);
                    setReadingDate(
                      new Date(moment(parsedObservation.effectiveDate).format('MM-DD-YYYY')),
                    );
                  }}
                  onClose={() => {
                    setSelectedObservation(null);
                    setA1CValue(null);
                    setReadingType(null);
                    setReadingDate(null);
                  }}
                  on="click"
                  position="bottom right"
                />
                <Icon
                  style={{ float: 'right' }}
                  size="large"
                  name="delete"
                  onClick={() => handleDelete(i)}
                />
              </div>
            </Label>
          }
          content={
            <span key={i}>
              <Icon name="clock" />
              {parsedObservation.readingType}
              {parsedObservation.readingType && '. '}
              {parsedObservation.source && <>Source: {parsedObservation.source}. </>}
              Last Updated By: {parsedObservation.author}.{' '}
              {moment(parsedObservation.lastUpdated).format('MMMM Do YYYY, h:mm:ss a')}.
            </span>
          }
          on="hover"
        />
      );
    } else {
      return (
        <Label
          key={'sidebar' + i}
          basic
          color={highlightID?.includes(observation?.resource?.id) ? 'teal' : 'grey'}
        >
          <div style={{ display: 'flex' }}>
            <div>
              <div>
                <b>
                  {parsedObservation.display}
                  {': '} {parsedObservation.quantity} {parsedObservation.unit}
                  {result ? (
                    <span className="bold" style={{ color: '#B03060' }}>
                      {' '}
                      ({result})
                    </span>
                  ) : (
                    ''
                  )}
                </b>
              </div>
              <div>{moment(parsedObservation.effectiveDate).format('MMMM Do YYYY, h:mm:ss a')}</div>
              {parsedObservation.source && <div>Source: {parsedObservation.source}</div>}
            </div>
          </div>
        </Label>
      );
    }
  };

  const handleDelete = (i: number) => {
    setLoading(true);

    const resource = !filterGlucose ? props.data[i]!.resource : glucoseOnly[i]!.resource;
    softDeleteObservation({
      observation: resource,
      token: authInfo.token,
      patientId: props.patientId,
      practitionerId: authInfo.practitionerId,
      practitionerName: authInfo.practitionerName,
      org: props.org,
    }).then(
      () => {
        setLoading(true);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
        props.mutate().then(() => {
          setLoading(false);
        });
      },
      () => {
        setLoading(false);
      },
    );
  };

  const graphData = glucoseOnly.map((observation: ResourceEntry<'Observation'>) => {
    const date = new Date(observation.resource.effectivePeriod!.start!).toLocaleDateString();

    return {
      x: date.substring(0, date.length - 5),
      // `Quantity.value` is `0..1`; react-vis plots `undefined` as a gap in the
      // line rather than throwing, which is the existing behaviour.
      y: observation.resource.valueQuantity!.value as number,
    };
  });

  const handleUpdate = (e: React.SyntheticEvent<HTMLElement>) => {
    e.preventDefault();
    setSelectedObservation(null);

    if (!a1cvalue || isNaN(Number(a1cvalue))) {
      return false;
    }
    setLoading(true);
    setLoadingMessage('Submitting lab Label...');

    /*
     * SUSPECTED DEFECT — REPORTED, NOT FIXED. The lab edit submit path assumes
     * three things the types say can be absent.
     *
     * `selectedObservation` is `useState(null)` and is only set by opening a row's
     * edit control, so submitting before that throws. `valueQuantity` and
     * `effectivePeriod` are both `0..1` on a FHIR Observation, and both are
     * WRITTEN THROUGH here (`body.valueQuantity.value = …`,
     * `body.effectivePeriod.start = …`) rather than read — so a lab record stored
     * without either fails the save after `setLoading(true)` has already run,
     * leaving the dialog spinning with no error shown.
     *
     * The `.find(...)` result on the extension arm is asserted for the same reason
     * the surrounding ternary exists: the arm is only taken when that exact `find`
     * has already returned a match.
     */
    let body: fhir4.Observation = selectedObservation!.resource;
    body.valueQuantity!.value = Number(a1cvalue);
    /* eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the rule's only fix is an `if`, which rewrites the expression rather than its type */
    body.extension
      ? body.extension.find(
          (e) => e.url === 'https://projectwell.io/fhir/identifiers/lab-reading-type',
        )
        ? (body.extension.find(
            (e) => e.url === 'https://projectwell.io/fhir/identifiers/lab-reading-type',
          )!.valueString = readingType as string)
        : body.extension.push({
            url: 'https://projectwell.io/fhir/identifiers/lab-reading-type',
            valueString: readingType as string,
          })
      : (body.extension = [
          {
            url: 'https://projectwell.io/fhir/identifiers/lab-reading-type',
            valueString: readingType as string,
          },
        ]);
    body.effectivePeriod!.start = readingDate
      ? moment(readingDate).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD');
    body = FHIRUtils.addUpdatedBy(body, authInfo.practitionerName);

    FHIRUpdater(`/Observation/${body.id}`, authInfo.token, body).then(
      () => {
        setLoadingMessage('Submission Success. Updating Lab UI...');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
        props.mutate().then(() => {
          setLoadingMessage('');
          setLoading(false);
          setA1CValue(null);
          setReadingDate(null);
          setReadingType(null);
        });
      },
      (error: RejectionWithMessage) => {
        setLoadingMessage(`Error - Unable to update Lab label: ${error.message}`);
        setLoading(false);
        setA1CValue(null);
        setReadingDate(null);
        setReadingType(null);
      },
    );
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (readingType === null) {
      setErrorMessage('Please select a reading type');
    } else {
      setErrorMessage(null);
      e.preventDefault();
      setPopupOpen(false);

      if (!a1cvalue || isNaN(Number(a1cvalue))) {
        return false;
      }
      setLoading(true);
      setLoadingMessage('Submitting lab Label...');

      const body: fhir4.Observation = ObservationBuilder.buildLabObservation(
        props.patientId,
        readingDate ? moment(readingDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
        '59261-8',
        'Hemoglobin A1c/Hemoglobin. total in Blood',
        Number(a1cvalue),
        '%',
        authInfo.practitionerName,
        readingType,
      );

      FHIRPoster(`/Observation/`, authInfo.token, body).then(
        () => {
          setLoadingMessage('Submission Success. Updating Lab UI...');
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
          props.mutate().then(() => {
            setLoadingMessage('');
            setLoading(false);
            setA1CValue(null);
            setReadingDate(null);
            setReadingType(null);
            setSelectedObservation(null);
          });
        },
        (error: RejectionWithMessage) => {
          setLoadingMessage(`Error - Unable to submit Lab label: ${error.message}`);
          setLoading(false);
          setA1CValue(null);
          setReadingDate(null);
          setReadingType(null);
          setSelectedObservation(null);
        },
      );
    }
  };

  return (
    <>
      {loading && <Loader active inline="centered" content={loadingMessage ?? ''} />}
      {props.data && (
        <>
          {!props.sidebar && (
            <div style={{ display: 'flex' }}>
              <Button.Group size="mini">
                <Button
                  content={'All'}
                  color={!filterGlucose ? 'blue' : 'grey'}
                  onClick={() => {
                    setGraphView(false);
                    setFilterGlucose(false);
                  }}
                />
                <Button
                  content={'Glucose ONLY'}
                  color={filterGlucose ? 'blue' : 'grey'}
                  onClick={() => setFilterGlucose(true)}
                />
              </Button.Group>

              <div style={{ width: '20px' }} />
              {filterGlucose && (
                <Button.Group size="mini">
                  <Button
                    content={'Labels'}
                    color={!graphView ? 'blue' : 'grey'}
                    onClick={() => setGraphView(false)}
                  />
                  <Button
                    content={'Graph'}
                    color={graphView ? 'blue' : 'grey'}
                    onClick={() => setGraphView(true)}
                  />
                </Button.Group>
              )}
            </div>
          )}

          {graphView && (
            <XYPlot width={900} height={250} xType="ordinal">
              <HorizontalGridLines />
              <LineMarkSeries
                data={graphData.reverse()}
                style={{
                  strokeWidth: '15px',
                  fill: 'none',
                }}
                lineStyle={{ stroke: 'orange' }}
                markStyle={{ stroke: 'teal', fill: 'teal' }}
              />
              <LabelSeries
                key={'glucoselabel'}
                data={graphData}
                // Dropping `getLabel` would render each point's own `label` field,
                // which these points do not carry, so the labels would disappear.
                getLabel={(d: { y: number }) => `${d.y}`}
                style={{ fill: 'white' }}
                labelAnchorX="middle"
                labelAnchorY="middle"
              />
              <XAxis />
              <YAxis />
            </XYPlot>
          )}
          {!graphView &&
            (!filterGlucose ? props.data : glucoseOnly).map((observation: ProfileObservationEntry, i: number) =>
              observationsList(observation, i, (!filterGlucose ? props.data : glucoseOnly).length),
            )}

          {!props.sidebar && (
            <Popup
              flowing
              trigger={<Button content={'+'} size="mini" compact />}
              content={
                <Form onSubmit={handleSubmit} textAlign="center">
                  <Form.Field>
                    <div
                      className="inline fields ui center aligned"
                      style={{
                        display: 'flex',
                        alignContent: 'stretch',
                        justifyContent: 'center',
                        alignSelf: 'right',
                        width: '110%',
                      }}
                    >
                      <Form.Input
                        placeholder="Enter A1C Value"
                        value={a1cvalue}
                        onChange={(e, fieldprops) => {
                          if (!isNaN(Number(fieldprops.value))) {
                            setA1CValue(fieldprops.value);
                            setErrorMessage(null);
                          } else {
                            setA1CValue(fieldprops.value);
                            setErrorMessage('Please enter a number');
                          }
                        }}
                        className="field fluid"
                        textAlign="center"
                        icon="lab"
                      />
                      {errorMessage && (
                        <Label basic color="red" pointing>
                          {errorMessage}
                        </Label>
                      )}
                      <Form.Dropdown
                        placeholder="Select Reading Type"
                        selection
                        selectOnBlur={false}
                        options={[
                          {
                            key: 'Self-reported',
                            text: 'Self-reported',
                            value: 'Self-reported',
                          },
                          {
                            key: 'Clinically validated',
                            text: 'Clinically validated',
                            value: 'Clinically validated',
                          },
                        ]}
                        onChange={handleSelect}
                      />
                      <Form.Input
                        value={readingDate}
                        onChange={(e, fieldprops) => {
                          setReadingDate(fieldprops.value);
                        }}
                        name="readingDate"
                        control={SemanticDatepicker}
                        format="YYYY-MM-DD"
                        placeholder="Enter Reading Date"
                        textAlign="center"
                      />
                    </div>
                    <div className="center aligned">
                      <Button.Group
                        style={{
                          display: 'flex',
                          alignContent: 'stretch',
                          justifyContent: 'center',
                          width: '100%',
                        }}
                        widths="2"
                      >
                        <Form.Button
                          style={{ textalign: 'center' }}
                          color="blue"
                          content="Create A1C Reading"
                          position="center"
                          className={isNaN(Number(a1cvalue)) ? 'disabled' : ''}
                          size="small"
                          textAlign="center"
                          fluid
                        />
                        {[
                          'IU',
                          'ZING',
                          'ANTHEM',
                          'BCMN-FEP',
                          'BCMN-MSHO-CHRO',
                          'BCMN-PMAP-CHRO',
                          'BCNC-EMPL-DM',
                          'BCNC-COMM-MH',
                          'AH-MCAID-HOUSE',
                          'BCNC-MCARE-DM',
                          'BCNC-IAFP-DM',
                          'BCNC-FEP',
                          'NLA-DM',
                          'NLA-MH',
                          'SCAN-MCARE-DM',
                          'SCHA-MCAID',
                          'ASPI-SDOH-HC',
                          'NRX',
                          'CFBC-MCAID',
                          'UHC-MCARE',
                          'HOR-MCAID-MLTSS',
                          'CNT-ACA',
                          'OPT-MA',
                          'JP',
                          // `props.org` is optional; `includes` compares by value, so
                          // an absent org simply does not match.
                        ].includes(props.org as string) && (
                          <>
                            <Button.Or />
                            <Form.Button
                              style={{ textalign: 'center' }}
                              fluid
                              textAlign="center"
                              size="small"
                              position="center"
                              color="blue"
                              content="Go to Blood Sugar Survey"
                              onClick={() => {
                                handleClose();
                                props.setActiveTab!('SURVEYS');
                                props.updateQRID!(
                                  props.org === 'IU' || props.org === 'ZING'
                                    ? `member-glucose-${props.org.toLowerCase()}`
                                    : 'member-glucose-default',
                                  true,
                                );
                              }}
                            />
                          </>
                        )}
                      </Button.Group>
                    </div>
                  </Form.Field>
                </Form>
              }
              open={popupOpen}
              onClose={handleClose}
              onOpen={handleOpen}
              on="click"
              position="bottom right"
            />
          )}
        </>
      )}
    </>
  );
}

export default Labs;
